import * as Sentry from '@sentry/react'

// RevenueCat REST API Types
export interface SubscriptionInfo {
  readonly billingIssuesDetectedAt: Date | null
  readonly expiresDate: Date | null
  readonly gracePeriodExpiresDate: Date | null
  readonly isActive: boolean
  readonly isSandbox: boolean
  readonly originalPurchaseDate: Date | null
  readonly periodType: 'NORMAL' | 'INTRO' | 'TRIAL'
  readonly purchaseDate: Date
  readonly refundedAt: Date | null
  readonly store:
    | 'app_store'
    | 'mac_app_store'
    | 'play_store'
    | 'amazon'
    | 'stripe'
    | 'rc_billing'
    | 'promotional'
    | 'paddle'
    | 'unknown'
  readonly storeTransactionId: string | null
  readonly unsubscribeDetectedAt: Date | null
  readonly willRenew: boolean
}

export interface CustomerInfo {
  readonly subscriptionsByProductIdentifier: {
    [productId: string]: SubscriptionInfo
  }
}

// RevenueCat REST API Configuration
const REVENUECAT_API_BASE = 'https://api.revenuecat.com/v1'

// Helper function to get API key
function getApiKey(): string {
  const apiKey = import.meta.env.VITE_REVENUECAT_API_KEY as string

  if (!apiKey) {
    const error = new Error(
      'VITE_REVENUECAT_API_KEY environment variable is not set',
    )
    Sentry.captureException(error)
    throw error
  }

  return apiKey
}

// Helper function to make authenticated requests to RevenueCat API
interface RevenueCatRawSubscription {
  billing_issues_detected_at: string | null
  expires_date: string | null
  grace_period_expires_date: string | null
  is_sandbox: boolean
  original_purchase_date: string | null
  period_type: SubscriptionInfo['periodType']
  purchase_date: string | null
  refunded_at: string | null
  store: SubscriptionInfo['store']
  store_transaction_id: string | null
  unsubscribe_detected_at: string | null
}

interface RevenueCatSubscriber {
  subscriptions?: Record<string, RevenueCatRawSubscription>
}

async function makeRevenueCatRequest(
  endpoint: string,
  options: RequestInit = {},
): Promise<Record<string, unknown>> {
  const apiKey = getApiKey()

  const response = await fetch(`${REVENUECAT_API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `RevenueCat API Error: ${response.status} ${response.statusText} - ${errorText}`,
    )
  }

  return response.json()
}

// Parse date strings from RevenueCat API responses
function parseRevenueCatDate(dateString: string | null): Date | null {
  return dateString ? new Date(dateString) : null
}

function checkActiveStatus(sub: RevenueCatRawSubscription): boolean {
  const expiresDate = parseRevenueCatDate(sub.expires_date)

  if (expiresDate === null) return false

  return expiresDate > new Date()
}

function checkRenewStatus(sub: RevenueCatRawSubscription): boolean {
  return !sub.unsubscribe_detected_at && checkActiveStatus(sub)
}

// Transform RevenueCat API subscription data to our SubscriptionInfo format
function transformSubscription(
  sub: RevenueCatRawSubscription,
): SubscriptionInfo {
  return {
    billingIssuesDetectedAt: parseRevenueCatDate(
      sub.billing_issues_detected_at,
    ),
    expiresDate: parseRevenueCatDate(sub.expires_date),
    gracePeriodExpiresDate: parseRevenueCatDate(sub.grace_period_expires_date),
    isActive: checkActiveStatus(sub),
    isSandbox: sub.is_sandbox || false,
    originalPurchaseDate: parseRevenueCatDate(sub.original_purchase_date),
    periodType: sub.period_type || 'NORMAL',
    purchaseDate: parseRevenueCatDate(sub.purchase_date) || new Date(),
    refundedAt: parseRevenueCatDate(sub.refunded_at),
    store: sub.store || 'unknown',
    storeTransactionId: sub.store_transaction_id,
    unsubscribeDetectedAt: parseRevenueCatDate(sub.unsubscribe_detected_at),
    willRenew: checkRenewStatus(sub),
  }
}

// Get customer information from RevenueCat REST API
async function getCustomerInfo(userId: string): Promise<CustomerInfo> {
  try {
    const response = await makeRevenueCatRequest(`/subscribers/${userId}`)
    const subscriber = (response.subscriber || {}) as RevenueCatSubscriber

    // Transform subscriptions
    const subscriptions: { [productId: string]: SubscriptionInfo } = {}
    if (subscriber.subscriptions) {
      for (const [productId, sub] of Object.entries(subscriber.subscriptions)) {
        subscriptions[productId] = transformSubscription(sub)
      }
    }

    return {
      subscriptionsByProductIdentifier: subscriptions,
    }
  } catch (error) {
    Sentry.captureException(
      `Failed to get customer info for user ${userId}: ${error}`,
    )
    throw error
  }
}

export async function getSubscriptions(
  userId: string,
): Promise<SubscriptionInfo[]> {
  try {
    const customerInfo = await getCustomerInfo(userId)

    return Object.values(customerInfo.subscriptionsByProductIdentifier)
  } catch (error) {
    Sentry.captureException(
      `Getting subscriptions failed for user ${userId}. Error: ${error}`,
    )
    return []
  }
}

export async function getSubscriptionStatus(
  userId?: string,
): Promise<'active' | 'inactive' | 'none'> {
  if (!userId) return 'none'

  try {
    const customerInfo = await getCustomerInfo(userId)

    // Check subscriptions for more direct subscription status
    const subscriptions = Object.values(
      customerInfo.subscriptionsByProductIdentifier,
    )

    if (subscriptions.length === 0) return 'none'

    // Check if any subscription is currently active
    const hasActiveSubscription = subscriptions.some((sub) => sub.isActive)

    return hasActiveSubscription ? 'active' : 'inactive'
  } catch (error) {
    Sentry.captureException(
      `Getting subscription status failed for user ${userId}. Error: ${error}`,
    )
    return 'none'
  }
}
